/// <reference path="./shapes.d.ts" />
import { PageGen, PdfPen, PdfBrush, PdfRect, PdfPoint, PdfColor, PdfUnit } from '../pdf';
import { GUtil } from '../gutil';

PageGen.prototype.drawRoad = function (
  rect: PdfRect,
  left: boolean,
  right: boolean,
  top: boolean,
  bottom: boolean,
  brush: PdfBrush | null = null
): void {
  const b = brush ?? PdfBrush.Black;
  const all = [left, right, top, bottom];
  const count = all.filter((i) => i).length;
  const matrix: boolean[][] = [
    [false, false, false],
    [false, false, false],
    [false, false, false],
  ];

  const center = new PdfPoint(rect.centerX, rect.centerY);
  const pen = new PdfPen(PdfColor.fromRgb(255, 255, 255), rect.height / 30);
  pen.dashArray = [2, 1];

  if (count > 0) matrix[1][1] = true;
  if (left) matrix[0][1] = true;
  if (right) matrix[2][1] = true;
  if (top) matrix[1][0] = true;
  if (bottom) matrix[1][2] = true;

  for (let y = 0; y < 3; y++) {
    for (let x = 0; x < 3; x++) {
      const r = new PdfRect(
        rect.x + (x * rect.width) / 3,
        rect.y + (y * rect.height) / 3,
        rect.width / 3,
        rect.height / 3
      );
      if (matrix[x][y]) {
        this.drawRectangle(null, b, r);
      }
    }
  }

  if (left) this.drawLine(pen, new PdfPoint(rect.left, center.y), center);
  if (right) this.drawLine(pen, center, new PdfPoint(rect.right, center.y));
  if (top) this.drawLine(pen, new PdfPoint(center.x, rect.top), center);
  if (bottom) this.drawLine(pen, center, new PdfPoint(center.x, rect.bottom));
};

PageGen.prototype.drawCloud = function (
  rect: PdfRect,
  brush: PdfBrush | null = null,
  pen: PdfPen | null = null
): void {
  const b = 0.7;
  const points: number[][] = [
    [0.18, 1.0, b, 0, 0, b],
    [0.0, 0.82, 0, b, b, 0],
    [0.12, 0.65, 0, b, b, 0],
    [0.3, 0.35, 0, b, b, 0],
    [0.6, 0.0, b / 2, 0, 0, b],
    [0.8, 0.4, b, 0, 0, b],
    [1.0, 0.7, 0, b, b, 0],
    [0.7, 1.0],
  ];

  const bezierPoints: PdfPoint[] = [];

  for (let i = 0; i < points.length - 1; i++) {
    const pp1 = points[i];
    const pp2 = points[i + 1];

    const p1 = new PdfPoint(rect.x + rect.width * pp1[0], rect.y + rect.height * pp1[1]);
    const p4 = new PdfPoint(rect.x + rect.width * pp2[0], rect.y + rect.height * pp2[1]);

    const p2 = new PdfPoint(p1.x + pp1[2] * (p4.x - p1.x), p1.y + pp1[3] * (p4.y - p1.y));
    const p3 = new PdfPoint(p4.x + pp1[4] * (p1.x - p4.x), p4.y + pp1[5] * (p1.y - p4.y));

    if (i === 0) {
      bezierPoints.push(p1);
    }
    bezierPoints.push(p2, p3, p4);
  }

  // Close the path
  const firstPoint = bezierPoints[0];
  bezierPoints.push(
    new PdfPoint(firstPoint.x, firstPoint.y),
    new PdfPoint(firstPoint.x, firstPoint.y),
    firstPoint
  );

  if (bezierPoints.length >= 4) {
    this.drawBeziers(pen, brush, bezierPoints);
  }
};

PageGen.prototype.drawFunnel = function (
  rect: PdfRect,
  brush: PdfBrush | null = null,
  pen: PdfPen | null = null
): void {
  const points: number[][] = [
    [0.0, 0.0],
    [1.0, 0.0],
    [0.55, 0.5],
    [0.55, 1.0],
    [0.45, 1.0],
    [0.45, 0.5],
  ];

  const polygonPoints = points.map(
    (p) => new PdfPoint(rect.x + p[0] * rect.width, rect.y + p[1] * rect.height)
  );

  this.drawPolygon(pen, brush, polygonPoints);
};

PageGen.prototype.drawCamera = function (
  rect: PdfRect,
  color: PdfColor | null = null,
  background: PdfColor | null = null
): void {
  const c = color ?? PdfColor.fromRgb(0, 0, 0);
  const b = background ?? PdfColor.fromRgb(255, 255, 255);

  const fill = new PdfBrush(c);
  const objPen = new PdfPen(b, rect.height / 20);
  const flBrush = new PdfBrush(b);

  const cornerRadius = rect.height * 0.2;
  const r0 = new PdfRect(rect.x, rect.y + rect.height * 0.15, rect.width, rect.height * 0.85);
  const r1 = new PdfRect(rect.x + rect.width * 0.25, rect.y, rect.width * 0.5, rect.height * 0.5);
  const c0 = PdfRect.circle(new PdfPoint(r0.centerX, r0.centerY), rect.height * 0.3);
  const c1 = PdfRect.circle(
    new PdfPoint(
      r0.right - rect.height * 0.25 + (rect.height * 0.15) / 2,
      r0.top + rect.height * 0.1 + (rect.height * 0.15) / 2
    ),
    (rect.height * 0.15) / 2
  );

  this.drawRoundedRectangle(null, fill, r0, cornerRadius);
  this.drawRoundedRectangle(null, fill, r1, cornerRadius);
  this.drawEllipse(objPen, flBrush, c0);
  this.drawEllipse(null, flBrush, c1);
};

PageGen.prototype.drawStart = function (rect: PdfRect, color: PdfColor | null = null): void {
  const c = color ?? PdfColor.fromRgb(0, 0, 0);
  const b = new PdfBrush(c);
  const p = new PdfPen(c, rect.height / 20);

  const points = [
    new PdfPoint(rect.x + rect.width * 0.3, rect.y + rect.height * 0.25),
    new PdfPoint(rect.x + rect.width * 0.3, rect.y + rect.height * 0.75),
    new PdfPoint(rect.x + rect.width * 0.8, rect.y + rect.height * 0.5),
  ];

  this.drawEllipse(p, null, rect);
  this.drawPolygon(null, b, points);
};

PageGen.prototype.drawStop = function (rect: PdfRect, color: PdfColor | null = null): void {
  const c = color ?? PdfColor.fromRgb(0, 0, 0);
  const b = new PdfBrush(c);
  const p = new PdfPen(c, rect.height / 20);

  const points = [
    new PdfPoint(rect.x + rect.width * 0.3, rect.y + rect.height * 0.3),
    new PdfPoint(rect.x + rect.width * 0.3, rect.y + rect.height * 0.7),
    new PdfPoint(rect.x + rect.width * 0.7, rect.y + rect.height * 0.7),
    new PdfPoint(rect.x + rect.width * 0.7, rect.y + rect.height * 0.3),
  ];

  this.drawEllipse(p, null, rect);
  this.drawPolygon(null, b, points);
};

PageGen.prototype.drawDice = function (pen: PdfPen, rect: PdfRect, count: number): void {
  if (count > 0) {
    const cornerRadius = Math.min(rect.width, rect.height) / 5;
    this.drawRoundedRectangle(pen, null, rect, cornerRadius);
  }

  const dot = (x: number, y: number) => {
    const posSize = new PdfRect(rect.x, rect.y, rect.width / 4, rect.height / 4);
    const dotWidth = rect.width / 6;
    const dotHeight = rect.height / 6;
    const dotRect = new PdfRect(
      rect.x + (x + 1) * posSize.width - dotWidth / 2,
      rect.y + (y + 1) * posSize.height - dotHeight / 2,
      dotWidth,
      dotHeight
    );
    this.drawEllipse(null, new PdfBrush(PdfColor.fromRgb(0, 0, 0)), dotRect);
  };

  switch (count) {
    case 1:
      dot(1, 1);
      break;
    case 2:
      dot(0, 0);
      dot(2, 2);
      break;
    case 3:
      dot(2, 0);
      dot(1, 1);
      dot(0, 2);
      break;
    case 4:
      dot(0, 0);
      dot(0, 2);
      dot(2, 0);
      dot(2, 2);
      break;
    case 5:
      dot(0, 0);
      dot(0, 2);
      dot(2, 0);
      dot(2, 2);
      dot(1, 1);
      break;
    case 6:
      dot(0, 0);
      dot(0, 1);
      dot(0, 2);
      dot(2, 0);
      dot(2, 1);
      dot(2, 2);
      break;
  }
};

PageGen.prototype.drawDicesH = function (
  pen: PdfPen,
  rect: PdfRect,
  margin: number,
  count: number,
  ...counts: number[]
): void {
  const sz = Math.min(
    (rect.width - (counts.length + 1) * margin) / counts.length,
    rect.height - 2 * margin
  );
  const origin = new PdfPoint(
    rect.x + (rect.width - counts.length * sz - (counts.length - 1) * margin) / 2,
    rect.y + (rect.height - sz) / 2
  );

  for (let i = 0; i < counts.length; i++) {
    const r = new PdfRect(origin.x + i * (margin + sz), origin.y, sz, sz);
    this.drawDice(pen, r, counts[i]);
  }
};

PageGen.prototype.drawDicesV = function (
  pen: PdfPen,
  rect: PdfRect,
  margin: number,
  ...counts: number[]
): void {
  const sz = Math.min(rect.width, (rect.height - (counts.length - 1) * margin) / counts.length);
  const origin = new PdfPoint(
    rect.x + (rect.width - sz) / 2,
    rect.y + (rect.height - counts.length * sz - (counts.length - 1) * margin) / 2
  );

  for (let i = 0; i < counts.length; i++) {
    const r = new PdfRect(origin.x, origin.y + i * (margin + sz), sz, sz);
    this.drawDice(pen, r, counts[i]);
  }
};

PageGen.prototype.drawClock = function (
  rect: PdfRect,
  h: number,
  m: number,
  s?: number | null,
  faceOnly?: boolean
): void {
  const margin = PdfUnit.FromMillimeters(2);
  const r0 = rect.centerSquare();
  const rect2 = new PdfRect(
    r0.x + margin,
    r0.y + margin,
    r0.width - 2 * margin,
    r0.height - 2 * margin
  );

  // Assuming GUtil.BasePen exists - if not, create a default black pen
  const basePen = (GUtil && GUtil.basePen) || new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);

  const center = PdfUnit.FromMillimeters(Math.min(rect2.width, rect2.height) * 0.03);
  const hourPen = new PdfPen(PdfColor.fromRgb(0, 0, 0), rect2.width * 0.05);
  const minutePen = new PdfPen(PdfColor.fromRgb(0, 0, 0), rect2.width * 0.03);

  this.drawEllipse(basePen, null, rect2);
  const diameter = rect2.width / 2;

  const P = (i: number, total: number, size: number): PdfPoint => {
    const angle = (2 * i * Math.PI) / total;
    return new PdfPoint(
      rect2.centerX + Math.sin(angle) * size,
      rect2.centerY - Math.cos(angle) * size
    );
  };

  // Draw hour markers
  for (let i = 0; i < 12; i++) {
    const p0 = P(i, 12, diameter);
    const p1 = P(i, 12, diameter * 0.8);
    this.drawLine(basePen, p0, p1);
  }

  // Draw minute markers
  for (let i = 0; i < 60; i++) {
    const p0 = P(i, 60, diameter);
    const p1 = P(i, 60, diameter * 0.9);
    this.drawLine(basePen, p0, p1);
  }

  // Draw center circle
  this.drawEllipse(
    null,
    new PdfBrush(PdfColor.fromRgb(0, 0, 0)),
    PdfRect.circle(new PdfPoint(rect2.centerX, rect2.centerY), center / 2)
  );

  // Skip drawing hands if faceOnly is true
  if (faceOnly) {
    return;
  }

  // Draw hour hand
  const hPoint = P(h + m / 60.0, 12, diameter * 0.5);
  this.drawLine(hourPen, new PdfPoint(rect2.centerX, rect2.centerY), hPoint);

  // Draw minute hand
  const mPoint = P(m + (s ?? 0) / 60.0, 60, diameter * 0.85);
  this.drawLine(minutePen, new PdfPoint(rect2.centerX, rect2.centerY), mPoint);

  // Draw second hand if provided
  if (s !== undefined && s !== null) {
    const sPoint = P(s, 60, diameter * 0.9);
    this.drawLine(basePen, new PdfPoint(rect2.centerX, rect2.centerY), sPoint);
  }
};

PageGen.prototype.drawHeart = function (
  pen: PdfPen | null,
  brush: PdfBrush | null,
  rect: PdfRect
): void {
  const {x} = rect;
  const {y} = rect;
  const w = rect.width;
  const h = rect.height;

  // Simple heart shape using cubic beziers
  const points: PdfPoint[] = [
    new PdfPoint(x + 0.5 * w, y + 0.3 * h), // Start top center
    new PdfPoint(x + 0.5 * w, y), // c1
    new PdfPoint(x, y), // c2
    new PdfPoint(x, y + 0.4 * h), // Left lobe

    new PdfPoint(x, y + 0.6 * h), // c1
    new PdfPoint(x + 0.3 * w, y + 0.8 * h), // c2
    new PdfPoint(x + 0.5 * w, y + h), // Bottom point

    new PdfPoint(x + 0.7 * w, y + 0.8 * h), // c1
    new PdfPoint(x + w, y + 0.6 * h), // c2
    new PdfPoint(x + w, y + 0.4 * h), // Right lobe

    new PdfPoint(x + w, y), // c1
    new PdfPoint(x + 0.5 * w, y), // c2
    new PdfPoint(x + 0.5 * w, y + 0.3 * h), // Back to start
  ];

  this.drawBeziers(pen, brush, points);
};
